import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDaysIcon,
  FunnelIcon,
  ChevronDownIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { format, subDays } from 'date-fns';

interface FiltersProps {
  selectedDate: Date;
  selectedCategory: string | null;
  categories: Array<{ name: string; count: number }>;
  onDateChange: (date: Date) => void;
  onCategoryChange: (category: string | null) => void;
  ageGroup: '6-9' | '10-13' | '14+';
}

const Filters: React.FC<FiltersProps> = ({
  selectedDate,
  selectedCategory,
  categories,
  onDateChange,
  onCategoryChange,
  ageGroup
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  // Last 7 days (today + 6 prior) — one week of history
  const getQuickDates = () =>
    Array.from({ length: 7 }, (_, i) => {
      const date = subDays(new Date(), i);
      const label = i === 0 ? 'Today' : i === 1 ? 'Yesterday' : `${i} Days Ago`;
      return { label, date };
    });

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      'SCIENCE': '🔬',
      'TECHNOLOGY': '💻',
      'EDUCATION': '📚',
      'NATURE': '🦋',
      'ENVIRONMENT': '🌱',
      'HEALTH': '❤️',
      'SPORTS': '⚽',
      'ARTS': '🎨',
      'CULTURE': '🎭',
      'ENTERTAINMENT': '🎮',
      'HISTORY': '📜',
      'POLITICS': '🏛️',
      'ECONOMICS': '💰',
      'SOCIETY': '👥',
      'FOOD': '🍔',
      'TRAVEL': '✈️',
      'TRANSPORTATION': '🚗',
      'SPACE': '🚀',
      'INNOVATION': '💡',
      'SAFETY': '🛡️',
      'WORLD_NEWS': '🌍',
      'MISCELLANEOUS': '📰',
    };
    return icons[category] || '📰';
  };

  const getFilterStyle = () => {
    if (ageGroup === '6-9') {
      return 'bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-300';
    }
    return 'bg-white border border-gray-200';
  };

  return (
    <div className={`${getFilterStyle()} rounded-xl p-3 md:p-4 shadow-lg hover:shadow-xl transition-shadow duration-300 mb-6`}>
      {/* Mobile Layout - Two Rows */}
      <div className="md:hidden space-y-3">
        {/* Row 1: Date and Category Filters */}
        <div className="flex items-center gap-2">
          {/* Date Picker */}
          <div className="relative flex-1">
            <button
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              className={`
                w-full flex items-center justify-between space-x-2 px-3 py-2 rounded-lg font-semibold text-sm
                ${ageGroup === '6-9'
                  ? 'bg-yellow-200 hover:bg-yellow-300 text-yellow-900 shadow-sm hover:shadow-md'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-sm hover:shadow-md'}
                transition-all duration-200 active:scale-98
              `}
            >
              <div className="flex items-center space-x-2 min-w-0">
                <CalendarDaysIcon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{format(selectedDate, 'EEE, MMM d')}</span>
              </div>
              <ChevronDownIcon className={`h-3 w-3 flex-shrink-0 transition-transform ${isDatePickerOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isDatePickerOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute z-40 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 p-2 min-w-[200px] max-h-72 overflow-y-auto"
                >
                  {getQuickDates().map((item) => (
                    <button
                      key={item.label}
                      onClick={() => {
                        onDateChange(item.date);
                        setIsDatePickerOpen(false);
                      }}
                      className={`
                        w-full text-left px-3 py-2 rounded-lg font-medium hover:bg-gray-100 transition-all duration-150
                        ${format(selectedDate, 'yyyy-MM-dd') === format(item.date, 'yyyy-MM-dd')
                          ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-700'}
                      `}
                    >
                      <span className="flex items-center justify-between">
                        {item.label}
                        {format(selectedDate, 'yyyy-MM-dd') === format(item.date, 'yyyy-MM-dd') &&
                          <CheckIcon className="h-4 w-4" />
                        }
                      </span>
                      <span className="text-xs text-gray-500">
                        {format(item.date, 'MMM d')}
                      </span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Category Filter */}
          <div className="relative flex-1">
            <button
              onClick={() => setIsCategoryOpen(!isCategoryOpen)}
              className={`
                w-full flex items-center justify-between space-x-2 px-3 py-2 rounded-lg font-semibold text-sm
                ${ageGroup === '6-9'
                  ? 'bg-orange-200 hover:bg-orange-300 text-orange-900 shadow-sm hover:shadow-md'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-sm hover:shadow-md'}
                transition-all duration-200 active:scale-98
              `}
            >
              <div className="flex items-center space-x-2 min-w-0">
                <FunnelIcon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{selectedCategory ? selectedCategory.replace('_', ' ') : 'All Categories'}</span>
              </div>
              <ChevronDownIcon className={`h-3 w-3 flex-shrink-0 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isCategoryOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  // right-0, not left-0: this trigger sits in the right half of
                  // the row, so a panel anchored to its left edge needed more
                  // width than the screen had and forced the whole page wider
                  // (a 250px panel starting at x=182 wants 432px on a 360px
                  // phone). Anchoring right makes it open leftward instead.
                  className="absolute right-0 z-40 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 p-2 min-w-[250px] max-w-[calc(100vw-2rem)] max-h-[400px] overflow-y-auto"
                >
                  <button
                    onClick={() => {
                      onCategoryChange(null);
                      setIsCategoryOpen(false);
                    }}
                    className={`
                      w-full text-left px-3 py-2 rounded-lg font-medium hover:bg-gray-100 transition-all duration-150
                      ${!selectedCategory ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-700'}
                    `}
                  >
                    <span className="flex items-center justify-between">
                      <span>
                        <span className="mr-2">📰</span>
                        All Categories
                      </span>
                      {!selectedCategory && <CheckIcon className="h-4 w-4" />}
                    </span>
                  </button>

                  <div className="my-1 border-t border-gray-200"></div>

                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => {
                        onCategoryChange(cat.name);
                        setIsCategoryOpen(false);
                      }}
                      className={`
                        w-full text-left px-3 py-2 rounded-lg font-medium hover:bg-gray-100 transition-all duration-150
                        ${selectedCategory === cat.name ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-700'}
                      `}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="flex items-center min-w-0">
                          <span className="mr-2 flex-shrink-0">{getCategoryIcon(cat.name)}</span>
                          <span className="truncate">{cat.name.replace('_', ' ')}</span>
                        </span>
                        <span className="flex items-center space-x-2 flex-shrink-0">
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {cat.count}
                          </span>
                          {selectedCategory === cat.name && <CheckIcon className="h-4 w-4" />}
                        </span>
                      </span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Row 2: Article Count - Subtle badge style */}
        <div className="flex items-center justify-center">
          <div className={`
            inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium
            ${ageGroup === '6-9'
              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
              : 'bg-blue-50 text-blue-700 border border-blue-200'}
          `}>
            <span className="mr-1.5">📰</span>
            <span>{categories.reduce((sum, cat) => sum + cat.count, 0)} Articles Today</span>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Original Horizontal Layout */}
      <div className="hidden md:flex items-center justify-between">
        {/* Date Picker - Left */}
        <div className="relative">
          <button
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            className={`
              flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-sm
              ${ageGroup === '6-9'
                ? 'bg-yellow-200 hover:bg-yellow-300 text-yellow-900 shadow-sm hover:shadow-md'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-sm hover:shadow-md'}
              transition-all duration-200 active:scale-98
            `}
          >
            <CalendarDaysIcon className="h-4 w-4" />
            <span>{format(selectedDate, 'EEEE, MMM d')}</span>
            <ChevronDownIcon className={`h-3 w-3 transition-transform ${isDatePickerOpen ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {isDatePickerOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute z-50 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 p-2 min-w-[200px]"
              >
                {getQuickDates().map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      onDateChange(item.date);
                      setIsDatePickerOpen(false);
                    }}
                    className={`
                      w-full text-left px-3 py-2 rounded-lg font-medium hover:bg-gray-100 transition-all duration-150
                      ${format(selectedDate, 'yyyy-MM-dd') === format(item.date, 'yyyy-MM-dd')
                        ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-700'}
                    `}
                  >
                    <span className="flex items-center justify-between">
                      {item.label}
                      {format(selectedDate, 'yyyy-MM-dd') === format(item.date, 'yyyy-MM-dd') &&
                        <CheckIcon className="h-4 w-4" />
                      }
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(item.date, 'MMM d')}
                    </span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Category Filter - Center */}
        <div className="relative">
          <button
            onClick={() => setIsCategoryOpen(!isCategoryOpen)}
            className={`
              flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-sm
              ${ageGroup === '6-9'
                ? 'bg-orange-200 hover:bg-orange-300 text-orange-900 shadow-sm hover:shadow-md'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-sm hover:shadow-md'}
              transition-all duration-200 active:scale-98
            `}
          >
            <FunnelIcon className="h-4 w-4" />
            <span>{selectedCategory ? selectedCategory.replace('_', ' ') : 'All Categories'}</span>
            <ChevronDownIcon className={`h-3 w-3 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {isCategoryOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute z-40 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 p-2 min-w-[250px] max-h-[400px] overflow-y-auto left-1/2 transform -translate-x-1/2"
              >
                <button
                  onClick={() => {
                    onCategoryChange(null);
                    setIsCategoryOpen(false);
                  }}
                  className={`
                    w-full text-left px-3 py-2 rounded-lg font-medium hover:bg-gray-100 transition-all duration-150
                    ${!selectedCategory ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-700'}
                  `}
                >
                  <span className="flex items-center justify-between">
                    <span>
                      <span className="mr-2">📰</span>
                      All Categories
                    </span>
                    {!selectedCategory && <CheckIcon className="h-4 w-4" />}
                  </span>
                </button>

                <div className="my-1 border-t border-gray-200"></div>

                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => {
                      onCategoryChange(cat.name);
                      setIsCategoryOpen(false);
                    }}
                    className={`
                      w-full text-left px-3 py-2 rounded-lg font-medium hover:bg-gray-100 transition-all duration-150
                      ${selectedCategory === cat.name ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-700'}
                    `}
                  >
                    <span className="flex items-center justify-between">
                      <span>
                        <span className="mr-2">{getCategoryIcon(cat.name)}</span>
                        {cat.name.replace('_', ' ')}
                      </span>
                      <span className="flex items-center space-x-2">
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {cat.count}
                        </span>
                        {selectedCategory === cat.name && <CheckIcon className="h-4 w-4" />}
                      </span>
                    </span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Article Count - Right */}
        <div className={`
          flex items-center px-4 py-2 rounded-full text-sm font-semibold
          ${ageGroup === '6-9'
            ? 'bg-yellow-50 text-yellow-700 border-2 border-yellow-200'
            : 'bg-blue-50 text-blue-700 border-2 border-blue-200'}
        `}>
          <span className="mr-2">📰</span>
          <span>{categories.reduce((sum, cat) => sum + cat.count, 0)} Articles Today</span>
        </div>
      </div>
    </div>
  );
};

export default Filters;